import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { X, Gift } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "smart_lead_popup_state_v1";
const COOLDOWN_DAYS = 7;
const SHOW_DELAY_MS = 8_000;

/**
 * Visitor Lead Capture popup.
 * - Shows once after 8s on /home for non-authenticated visitors
 * - Stores dismissal in localStorage with 7-day cooldown
 * - On capture: stores lead in DB, fires a Resend nudge with code ROYAL10
 */
export default function VisitorLeadPopup() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [contact, setContact] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (user) return; // signed-in users never see it
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const { until } = JSON.parse(raw);
                if (until && new Date(until) > new Date()) return;
            }
        } catch (e) { /* noop */ }
        const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
        return () => clearTimeout(t);
    }, [user]);

    const persistDismissal = (days = COOLDOWN_DAYS) => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ until: new Date(Date.now() + days * 86400_000).toISOString() }),
            );
        } catch (e) { /* noop */ }
    };

    const close = () => {
        persistDismissal();
        setOpen(false);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const v = contact.trim();
        if (!v) {
            toast.error("Enter your email or phone to claim 10% off");
            return;
        }
        const isEmail = v.includes("@");
        setSubmitting(true);
        try {
            await api.post("/leads/capture", {
                email: isEmail ? v : null,
                phone: isEmail ? null : v,
                source: "landing_popup",
            });
            setDone(true);
            persistDismissal(365); // never re-show after a successful capture
            toast.success(isEmail ? "Check your inbox — code ROYAL10 is on the way." : "Saved. We'll text you ROYAL10 soon.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-32px))] animate-in fade-in slide-in-from-bottom-4 duration-500"
            data-testid="visitor-lead-popup"
        >
            <div className="bg-card/95 backdrop-blur border border-primary/40 rounded-sm p-6 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/10 blur-3xl" />
                <button
                    type="button"
                    onClick={close}
                    className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="lead-popup-close"
                    aria-label="Close"
                >
                    <X size={16} strokeWidth={1.5} />
                </button>
                <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                        <Gift size={14} className="gold-text" strokeWidth={1.5} />
                        <div className="overline">Welcome offer</div>
                    </div>
                    {!done ? (
                        <>
                            <h3 className="font-serif text-2xl leading-tight">
                                Want <span className="italic gold-grad">10% off</span> your first Royal Clean?
                            </h3>
                            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                                Enter your email or phone — we'll send a code, no spam.
                            </p>
                            <form onSubmit={onSubmit} className="mt-4 space-y-2.5">
                                <input
                                    type="text"
                                    inputMode="email"
                                    placeholder="you@email.com or +1 876 ..."
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                    className="w-full bg-input border border-white/15 px-3 py-2.5 rounded-sm text-sm focus:border-primary focus:outline-none transition-colors"
                                    data-testid="lead-popup-input"
                                    autoFocus
                                />
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                    data-testid="lead-popup-submit"
                                >
                                    {submitting ? "Sending..." : "Claim My 10% Off"}
                                </Button>
                            </form>
                            <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground/70">
                                No spam. Unsubscribe any time.
                            </p>
                        </>
                    ) : (
                        <>
                            <h3 className="font-serif text-2xl leading-tight" data-testid="lead-popup-success">
                                Welcome to <span className="italic gold-grad">SMART.</span>
                            </h3>
                            <p className="mt-3 text-sm text-foreground/90 leading-relaxed">
                                Your code <span className="gold-text font-mono">ROYAL10</span> is waiting in your inbox.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
